import { type FC } from "react"

import { landingPageFonts } from "@forento/core/models/landing"
import type { LandingPageBrandColor } from "@forento/core/models/landingStyle"
import { type LandingPageStyleId, landingPageStyleIds, landingPageStyles } from "@forento/core/models/landingStyle"
import { getLandingThemeStyle } from "@forento/ui/utilities/landingTheme"
import { cn } from "@forento/ui/utilities/tailwind"

type Props = {
	value: LandingPageStyleId
	brandColor: LandingPageBrandColor
	usesBrandColor: boolean
	isDisabled?: boolean
	className?: string
	onChange(styleId: LandingPageStyleId): void
}
const WebsiteStylePicker: FC<Props> = ({
	value,
	brandColor,
	usesBrandColor,
	isDisabled = false,
	className,
	onChange,
}) => (
	<div className={cn("grid grid-cols-2 gap-2", className)}>
		{landingPageStyleIds.map(styleId => {
			const style = landingPageStyles[styleId]
			const isSelected = styleId === value
			return (
				<button
					key={styleId}
					type="button"
					aria-pressed={isSelected}
					title={style.description}
					disabled={isDisabled}
					onClick={() => onChange(styleId)}
					className={cn(
						"flex cursor-pointer flex-col gap-1.5 rounded-lg border-2 p-1.5 text-left transition-colors disabled:cursor-auto",
						isSelected ? "border-primary" : "border-black/10 hover:border-gray-300",
					)}
				>
					<div
						className="landing-page overflow-hidden rounded-md ring-1 ring-black/5"
						style={getLandingThemeStyle(brandColor, {
							styleId,
							usesBrandColor,
							fontId: style.fontId,
							buttonStyleId: style.buttonStyleId,
						})}
					>
						<div className="bg-landing-header flex items-center justify-between px-2 py-1.5">
							<span className="bg-landing-header-foreground/70 h-1.5 w-5 rounded-full" />
							<span className="flex gap-1">
								<span className="bg-landing-header-foreground/30 h-1 w-3 rounded-full" />
								<span className="bg-landing-header-foreground/30 h-1 w-3 rounded-full" />
							</span>
						</div>
						<div className="flex flex-col gap-1.5 px-2 pt-2.5 pb-2">
							<span
								className="text-landing-foreground text-sm leading-none font-bold"
								style={{ fontFamily: landingPageFonts[style.fontId].headingFamily }}
							>
								Aa
							</span>
							<span className="bg-landing-foreground-muted/50 h-1 w-10 rounded-full" />
							<span className="bg-landing-foreground-muted/50 h-1 w-7 rounded-full" />
							<span className="landing-button mt-1 h-3.5 w-8" />
						</div>
						<div className="flex gap-1 px-2 pb-2">
							<span className="bg-landing-surface ring-landing-border h-4 flex-1 rounded-sm ring-1" />
							<span className="bg-landing-surface ring-landing-border h-4 flex-1 rounded-sm ring-1" />
						</div>
						<div className="bg-landing-footer h-2.5" />
					</div>
					<span className={cn("px-0.5 text-xs font-semibold", isSelected ? "text-primary" : "text-gray-700")}>
						{style.label}
					</span>
				</button>
			)
		})}
	</div>
)

export default WebsiteStylePicker
